import { useState } from "react";
import { Link } from "react-router-dom";
import { ScholarLayout } from "@jambushrushti/components/scholar/ScholarLayout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Search,
  Users,
  FileText,
  Plus,
  MessageSquare,
  Calendar,
  CheckCircle,
  Clock,
  BookOpen,
  Handshake,
  UserPlus,
  Upload,
  File,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { FileShareDialog } from "@jambushrushti/components/scholar/FileShareDialog";

const collaborationProjects = [
  {
    id: "1",
    title: "Comparative Analysis of Jain and Buddhist Karma Theories",
    description:
      "A joint research project exploring the philosophical nuances between Jain and Buddhist concepts of Karma.",
    author: {
      id: "1",
      name: "Dr. Rajesh Sharma",
      avatar: "/src/assets/scholars/scholar-male-1.jpg",
      affiliation: "Gujarat University",
    },
    collaborators: 3,
    status: "active",
    publications: 2,
    createdAt: "2024-01-15",
    tags: ["Jain Philosophy", "Buddhism", "Karma"],
    files: [
      { id: "f1", name: "Research Proposal.pdf", size: "2.4 MB" },
      { id: "f2", name: "Literature Review.docx", size: "1.8 MB" },
    ],
  },
  {
    id: "2",
    title: "Digital Preservation of 15th Century Jain Manuscripts",
    description:
      "Collaborative effort to digitize and preserve rare Jain manuscripts with modern annotation tools.",
    author: {
      id: "2",
      name: "Prof. Meera Patel",
      avatar: "/src/assets/scholars/scholar-female-1.jpg",
      affiliation: "SOAS London",
    },
    collaborators: 5,
    status: "active",
    publications: 4,
    createdAt: "2023-11-20",
    tags: ["Manuscripts", "Digitization", "Preservation"],
    files: [{ id: "f3", name: "Digitization Guidelines.pdf", size: "3.2 MB" }],
  },
];

const collaborationRequests = [
  {
    id: "1",
    from: {
      name: "Dr. Sunita Devi",
      avatar: "/src/assets/scholars/scholar-female-2.jpg",
      affiliation: "Banaras Hindu University",
    },
    project: "Jain Iconography in Temple Architecture",
    message:
      "I would like to collaborate on documenting Jain iconographic elements in medieval temples.",
    status: "pending",
    date: "2024-03-10",
  },
];

export default function ScholarCollaborate() {
  const [searchQuery, setSearchQuery] = useState("");
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [projectTitle, setProjectTitle] = useState("");
  const [projectDescription, setProjectDescription] = useState("");
  const [fileShareOpen, setFileShareOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<
    (typeof collaborationProjects)[0] | null
  >(null);

  const filteredProjects = collaborationProjects.filter(
    project =>
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateProject = () => {
    if (!projectTitle.trim()) {
      toast.error("Please enter a project title");
      return;
    }
    toast.success("Collaboration project created successfully!");
    setProjectTitle("");
    setProjectDescription("");
    setNewProjectOpen(false);
  };

  const handleJoinProject = (title: string) =>
    toast.success(`Request sent to join "${title}"`);
  const handleAcceptRequest = () =>
    toast.success("Collaboration request accepted!");
  const handleDeclineRequest = () =>
    toast.info("Collaboration request declined");
  const handleShareFile = (project: (typeof collaborationProjects)[0]) => {
    setSelectedProject(project);
    setFileShareOpen(true);
  };

  return (
    <ScholarLayout title="Collaborate">
      <div className="space-y-6">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-heading flex items-center gap-2 text-xl font-semibold">
              <Handshake className="h-6 w-6 text-primary" />
              Scholar Collaboration
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Connect with scholars and work together on research projects
            </p>
          </div>
          <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
            <DialogTrigger asChild>
              <Button variant="hero">
                <Plus className="mr-2 h-4 w-4" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Collaboration Project</DialogTitle>
                <DialogDescription>
                  Start a new collaboration project and invite scholars to join.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Project Title</Label>
                  <Input
                    placeholder="Enter project title..."
                    value={projectTitle}
                    onChange={e => setProjectTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Describe your project..."
                    value={projectDescription}
                    onChange={e => setProjectDescription(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setNewProjectOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleCreateProject}>Create Project</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="projects">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="requests">Requests</TabsTrigger>
            <TabsTrigger value="my-projects">My Projects</TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="space-y-6">
            <Card>
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {filteredProjects.map(project => (
                <Card
                  key={project.id}
                  className="transition-all hover:shadow-lg"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-4 md:flex-row">
                      <div className="flex-1">
                        <Badge
                          variant={
                            project.status === "active"
                              ? "default"
                              : "secondary"
                          }
                          className="mb-2"
                        >
                          {project.status === "active" ? (
                            <>
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Active
                            </>
                          ) : (
                            <>
                              <UserPlus className="mr-1 h-3 w-3" />
                              Seeking
                            </>
                          )}
                        </Badge>
                        <h3 className="font-heading mb-2 text-lg font-semibold">
                          {project.title}
                        </h3>
                        <p className="mb-4 text-sm text-muted-foreground">
                          {project.description}
                        </p>
                        <div className="mb-4 flex flex-wrap gap-2">
                          {project.tags.map(tag => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {project.collaborators}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="h-4 w-4" />
                            {project.publications}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(project.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex min-w-[200px] flex-col items-start gap-4 md:items-end">
                        <Link
                          to={`/scholar/profile/${project.author.id}`}
                          className="flex items-center gap-3 hover:opacity-80"
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={project.author.avatar} />
                            <AvatarFallback>
                              {project.author.name
                                .split(" ")
                                .map(n => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {project.author.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {project.author.affiliation}
                            </p>
                          </div>
                        </Link>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleShareFile(project)}
                          >
                            <Upload className="mr-1 h-4 w-4" />
                            Share
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleJoinProject(project.title)}
                          >
                            <UserPlus className="mr-1 h-4 w-4" />
                            Join
                          </Button>
                        </div>
                        {project.files.length > 0 && (
                          <div className="mt-2 w-full border-t pt-2">
                            <p className="mb-2 text-xs text-muted-foreground">
                              Files ({project.files.length})
                            </p>
                            {project.files.slice(0, 2).map(file => (
                              <div
                                key={file.id}
                                className="mb-1 flex items-center justify-between rounded bg-muted/50 p-2 text-xs"
                              >
                                <span className="flex items-center gap-1">
                                  <File className="h-3 w-3" />
                                  {file.name}
                                </span>
                                <Download className="h-3 w-3 cursor-pointer" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="space-y-4">
            {collaborationRequests.map(request => (
              <Card key={request.id}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={request.from.avatar} />
                      <AvatarFallback>
                        {request.from.name
                          .split(" ")
                          .map(n => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h4 className="font-semibold">{request.from.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {request.from.affiliation}
                      </p>
                      <p className="mt-2 font-medium">{request.project}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {request.message}
                      </p>
                      <div className="mt-4 flex gap-2">
                        <Button size="sm" onClick={handleAcceptRequest}>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeclineRequest}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="my-projects">
            <Card>
              <CardContent className="py-16 text-center">
                <BookOpen className="mx-auto mb-4 h-16 w-16 text-muted-foreground/30" />
                <h3 className="font-heading mb-2 text-xl font-semibold">
                  No projects yet
                </h3>
                <p className="mb-6 text-muted-foreground">
                  Create your first collaboration project
                </p>
                <Button variant="hero" onClick={() => setNewProjectOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Project
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <FileShareDialog
        open={fileShareOpen}
        onOpenChange={setFileShareOpen}
        projectTitle={selectedProject?.title || ""}
      />
    </ScholarLayout>
  );
}
